/**
 * Phase 7 Commit J. Slot-hold countdown strip.
 *
 * Pinned at the top of `.sbw-stage` from the Time step onward. Reads
 * `expiresAt` from the cart (Square reserveSlot returns this). Updates
 * once per second — the displayed countdown converts a passive 8-minute
 * Square hold into active urgency that drives commitment through the
 * remaining steps.
 *
 * Hidden when `expiresAt` is null (slot not yet reserved) or the hold
 * has already expired (parent should redirect back to Time when this
 * fires "expired").
 */

import { useEffect, useState } from 'preact/hooks';

interface Props {
  /** When the slot hold expires (ISO string). */
  expiresAt: string | null;
  /** Human-readable label for what's being held (e.g. "Sat 7:30 PM"). */
  whenLabel: string;
  /** Fired when the countdown hits zero — caller routes back to Time. */
  /**
   * Called once when the countdown reaches zero. Return `false` to say
   * "not now" — the expiry stays armed and fires again on the next
   * tick. Anything else latches it, and the strip unmounts.
   */
  onExpired?: () => boolean | void;
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function HoldStrip({ expiresAt, whenLabel, onExpired }: Props) {
  const [now, setNow] = useState(() => Date.now());
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;
    setHasExpired(false);
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  // Fire onExpired side-effect from useEffect (not render) so React's
  // double-invocation in StrictMode + parent re-renders don't queue
  // duplicate calls. Latches via hasExpired so the callback runs once.
  //
  // The latch follows what the parent DID, not merely that it was
  // called. It refuses to navigate while a payment is being confirmed
  // (see lib/payment-lock), and latching on that suppression left the
  // customer with an expired hold, no countdown — the strip unmounts at
  // zero — and, if the card was then declined, a message about the card
  // that said nothing about the time. Returning false keeps this armed
  // so it fires again on the next tick once the payment settles.
  useEffect(() => {
    if (!expiresAt || hasExpired) return;
    const expiryMs = new Date(expiresAt).getTime();
    if (now >= expiryMs) {
      if (onExpired?.() === false) return;
      setHasExpired(true);
    }
  }, [expiresAt, now, hasExpired, onExpired]);

  if (!expiresAt) return null;
  const expiryMs = new Date(expiresAt).getTime();
  const remaining = expiryMs - now;
  if (remaining <= 0) return null;

  return (
    <div class="sbw-hold-strip" role="status" aria-live="polite">
      <span class="sbw-hold-dot" aria-hidden="true" />
      <span>
        <b>Holding {whenLabel}</b> — expires in{' '}
        <span class="sbw-hold-tnum">{formatRemaining(remaining)}</span>
      </span>
    </div>
  );
}
